import {
    StyleFunction,
    useAuth,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    GQApprovalTokenGroupInput,
    useApprovalTokenGroupCreateMutation,
} from "graphql-sdk";
import { defaultApprovalTokenGroup, schemaApprovalTokenGroup } from "lib";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks";
import { RootStackScreenProps } from "../../../../navigation";
import { AdminContainer } from "../../../container";
import { Form } from "../form";

type ScreenProps = RootStackScreenProps<"APPROVAL_TOKEN_CREATE">;

export function ApprovalTokenGroupCreateScreen({
    navigation: { navigate },
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.approval");

    const [t] = useTranslation();
    const [approvalTokenGroupCreate] = useApprovalTokenGroupCreateMutation();
    const form = useForm<GQApprovalTokenGroupInput>(
        schemaApprovalTokenGroup,
        defaultApprovalTokenGroup
    );

    const [{ values }, { setDefaultValues, reset }] = form;
    const styles = useThemedStyle(styleFunc);
    const { handleMutationError } = useHandleMutationError();

    const handleCreate = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(
            async () =>
                await approvalTokenGroupCreate({
                    variables: {
                        approvalTokenGroup: {
                            name: values.name,
                            active: values.active,
                        },
                    },
                }),
            t("common.saved", "Saved"),
            () => {
                reset();
                navigate("APPROVALS", { refetch: true, tabIndex: 2 });
            }
        );
    }, [
        values,
        handleMutationError,
        t,
        approvalTokenGroupCreate,
        reset,
        navigate,
    ]);

    useEffect(() => {
        if (values) {
            return;
        }

        setDefaultValues({
            name: "",
            active: true,
        });
    }, [setDefaultValues, values]);

    return (
        <AdminContainer>
            <View>
                <View style={styles.formBox}>
                    <Form
                        form={form}
                        onSubmit={handleCreate}
                        submitButton={["common.save", "Save"]}
                    />
                </View>
            </View>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    formBox: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
